/**
 * Theme Helper Utilities
 * Functions for applying themes to the document and managing theme transitions
 */

import { CSSCustomProperties, ThemeConfig } from '../tokens';

// Apply CSS custom properties to document
export const applyThemeToDocument = (
  cssCustomProperties: CSSCustomProperties,
  config: ThemeConfig
): void => {
  const documentElement = document.documentElement;

  // Apply transition class if enabled
  if (config.enableTransitions) {
    documentElement.style.setProperty(
      '--theme-transition-duration',
      `${config.transitionDuration}ms`
    );

    // Add transition styles
    const transitionStyles = [
      'color',
      'background-color',
      'border-color',
      'box-shadow',
      'fill',
      'stroke'
    ].join(', ');

    documentElement.style.setProperty(
      '--theme-transition-properties',
      transitionStyles
    );
  }

  // Apply all CSS custom properties to document root
  Object.entries(cssCustomProperties).forEach(([property, value]) => {
    documentElement.style.setProperty(property, String(value));
  });

};

// Remove theme-related CSS properties from document
export const removeThemeFromDocument = (): void => {
  const documentElement = document.documentElement;
  const styles = documentElement.style;
  
  // Remove theme-related custom properties
  for (let i = styles.length - 1; i >= 0; i--) {
    const property = styles[i];
    if (property.startsWith('--primitive-') || 
        property.startsWith('--semantic-') || 
        property.startsWith('--component-') ||
        property.startsWith('--theme-')) {
      documentElement.style.removeProperty(property);
    }
  }
};

// Set theme attribute on document for CSS targeting
export const setThemeAttribute = (themeName: string): void => {
  document.documentElement.setAttribute('data-theme', themeName);
};

// Remove theme attribute from document
export const removeThemeAttribute = (): void => {
  document.documentElement.removeAttribute('data-theme');
};

// Detect if user prefers reduced motion
export const prefersReducedMotion = (): boolean => {
  if (!window.matchMedia) {
    return false;
  }
  
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
};

// Get computed CSS custom property value
export const getCSSCustomProperty = (propertyName: string): string => {
  const value = getComputedStyle(document.documentElement)
    .getPropertyValue(propertyName)
    .trim();
  
  return value;
};

// Set individual CSS custom property
export const setCSSCustomProperty = (propertyName: string, value: string): void => {
  document.documentElement.style.setProperty(propertyName, value);
};

// Batch update CSS custom properties for performance
export const batchUpdateCSSProperties = (
  properties: CSSCustomProperties
): void => {
  // Use requestAnimationFrame to batch DOM updates
  requestAnimationFrame(() => {
    const documentElement = document.documentElement;
    
    Object.entries(properties).forEach(([property, value]) => {
      documentElement.style.setProperty(property, String(value));
    });
  });
};

// Theme transition effect
export const performThemeTransition = (
  newProperties: CSSCustomProperties,
  config: ThemeConfig,
  onComplete?: () => void
): void => {
  if (!config.enableTransitions || prefersReducedMotion()) {
    // Apply immediately without transition
    batchUpdateCSSProperties(newProperties);
    onComplete?.();
    return;
  }

  // Apply new properties with transition
  batchUpdateCSSProperties(newProperties);
  
  // Call completion callback after transition duration
  if (onComplete) {
    setTimeout(onComplete, config.transitionDuration);
  }
};

// Color utility functions
export const hexToRgb = (hex: string): { r: number; g: number; b: number } | null => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16)
  } : null;
};

export const rgbToHex = (r: number, g: number, b: number): string => {
  return "#" + [r, g, b].map(x => {
    const hex = x.toString(16);
    return hex.length === 1 ? "0" + hex : hex;
  }).join("");
};

// Lighten/darken color utilities
export const lightenColor = (color: string, amount: number): string => {
  const rgb = hexToRgb(color);
  if (!rgb) return color;
  
  const newR = Math.min(255, Math.round(rgb.r + (255 - rgb.r) * amount));
  const newG = Math.min(255, Math.round(rgb.g + (255 - rgb.g) * amount));
  const newB = Math.min(255, Math.round(rgb.b + (255 - rgb.b) * amount));
  
  return rgbToHex(newR, newG, newB);
};

export const darkenColor = (color: string, amount: number): string => {
  const rgb = hexToRgb(color);
  if (!rgb) return color;
  
  const newR = Math.max(0, Math.round(rgb.r * (1 - amount)));
  const newG = Math.max(0, Math.round(rgb.g * (1 - amount)));
  const newB = Math.max(0, Math.round(rgb.b * (1 - amount)));
  
  return rgbToHex(newR, newG, newB);
};

// Theme debugging utilities
export const debugThemeProperties = (): void => {
  const documentElement = document.documentElement;
  const computedStyles = getComputedStyle(documentElement);
  const themeProperties: Record<string, string> = {};
  
  // Collect all theme-related CSS custom properties
  for (let i = 0; i < computedStyles.length; i++) {
    const property = computedStyles[i];
    if (property.startsWith('--primitive-') || 
        property.startsWith('--semantic-') || 
        property.startsWith('--component-')) {
      themeProperties[property] = computedStyles.getPropertyValue(property).trim();
    }
  }
  
  console.group('🎨 Current Theme Properties');
  console.table(themeProperties);
  console.groupEnd();
};

// Validate theme application
export const validateThemeApplication = (
  expectedProperties: CSSCustomProperties
): { isValid: boolean; missingProperties: string[]; incorrectValues: string[] } => {
  const missingProperties: string[] = [];
  const incorrectValues: string[] = [];
  
  Object.entries(expectedProperties).forEach(([property, expectedValue]) => {
    const actualValue = getCSSCustomProperty(property);
    
    if (!actualValue) {
      missingProperties.push(property);
    } else if (actualValue !== String(expectedValue)) {
      incorrectValues.push(`${property}: expected "${expectedValue}", got "${actualValue}"`);
    }
  });
  
  return {
    isValid: missingProperties.length === 0 && incorrectValues.length === 0,
    missingProperties,
    incorrectValues,
  };
};
